// @flow
import { useDispatch, useSelector } from 'react-redux';

import { Portal } from '@components/Portal';

import FormattedPrice from '@components/FormattedPrice';
import ProgressiveImage from '../ProgressiveImage';

import { detailPath, searchPath } from '@src/app_routes/settings';

import css from '@styles/dyorDetail';

import type { DyorSettingType } from '@types/setting';

type PropsType = {
  show: boolean,
  setting: DyorSettingType,
  chainPosition: number,
  containerRef: HTMLElement
};

type TImageProps = { setting: DyorSettingType };
type TContentProps = TImageProps;
type TDescriptionProps = TImageProps;
type TPriceProps = TImageProps;
type TLinksProps = TImageProps;

function Image({ setting }: TImageProps) {
  return (
    <div className={css.detailImage}>
      <ProgressiveImage
        images={[
          { src: setting.incomplete_image.thumbnail, alt: 'Setting preview' }
        ]}
      />
    </div>
  );
}

function Content({ setting }: TContentProps) {
  return (
    <div>
      <Description setting={setting} />
      <Price setting={setting} />
      <Links setting={setting} />
    </div>
  );
}

function Description({ setting }: TDescriptionProps) {
  return <p>{setting.short_description}</p>;
}

function Price({ setting }: TPriceProps) {
  return (
    <p className={css.detailPrice}>
      <FormattedPrice value={setting.retail_price} />
      <ClearLink />
    </p>
  );
}

function ClearLink() {
  const dispatch = useDispatch();
  const onClearClick = React.useCallback(() => {
    dispatch({
      type: 'CLEAR_DYOR_SETTING',
      callback: () => (window.location = searchPath())
    });
  }, []);

  return (
    <a onClick={onClearClick} className={css.deleteLink}>
      delete
    </a>
  );
}

function Links({ setting }: TLinksProps) {
  return (
    <div className={css.links}>
      <a className={css.viewLink} href={detailPath(setting)}>
        VIEW
      </a>
      <a className={css.changeLink} href={searchPath()}>
        CHANGE
      </a>
    </div>
  );
}

export default React.forwardRef(
  ({ setting, show, chainPosition, containerRef }: PropsType, ref) => {
    if (!show || !setting) return null;

    return (
      <Portal htmlId='dyor-detail-popover'>
        <div
          className={css.detail({
            chainPosition,
            containerWidth: containerRef.current.offsetWidth
          })}
          ref={ref}
        >
          <Image setting={setting} />
          <Content setting={setting} />
        </div>
      </Portal>
    );
  }
);
